import React, { useState } from 'react';
import styled from 'styled-components';
import { Badge } from '@bigcommerce/big-design';

const CardContainer = styled.div`
	border: 1px solid #d9dce9;
	border-radius: 8px;
	overflow: hidden;
	cursor: pointer;
	transition: box-shadow 0.2s;

	&:hover {
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
	}
`;

const ThumbnailWrapper = styled.div`
	width: 100%;
	padding-top: 75%;
	position: relative;
	background-color: #f0f0f0;
`;

const ThumbnailImg = styled.img`
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	object-fit: cover;
`;

const VideoThumb = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background-color: #1a1a1a;

	&::after {
		content: '▶';
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		font-size: 20px;
		color: #fff;
		background: rgba(0, 0, 0, 0.5);
		width: 36px;
		height: 36px;
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
		padding-left: 3px;
	}
`;

const CardNav = styled.button`
	position: absolute;
	top: 50%;
	transform: translateY(-50%);
	z-index: 2;
	background: rgba(255, 255, 255, 0.85);
	border: none;
	border-radius: 50%;
	width: 48px;
	height: 48px;
	font-size: 24px;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);

	&:hover {
		background: #fff;
	}

	${(props) => (props.$left ? 'left: 4px;' : 'right: 4px;')}
`;

const CardDots = styled.div`
	position: absolute;
	bottom: 6px;
	left: 50%;
	transform: translateX(-50%);
	display: flex;
	gap: 4px;
	z-index: 2;
`;

const CardDot = styled.div`
	width: 6px;
	height: 6px;
	border-radius: 50%;
	background: ${(props) =>
		props.$active ? '#fff' : 'rgba(255, 255, 255, 0.5)'};
	transition: background 0.2s;
`;

const CardBody = styled.div`
	padding: 12px;
`;

const Name = styled.div`
	font-weight: 600;
	font-size: 14px;
	margin-bottom: 4px;
`;

const OrderId = styled.div`
	font-size: 13px;
	color: #6b6f82;
	margin-bottom: 8px;
`;

const STATUS_VARIANT = {
	pending: 'warning',
	approved: 'success',
	rejected: 'danger',
};

export default function PhotoContestCard({ submission, onClick }) {
	const [index, setIndex] = useState(0);

	const mediaItems = [];
	(submission.photoUrls || []).forEach((url) =>
		mediaItems.push({ type: 'photo', url }),
	);
	if (submission.videoUrl) {
		mediaItems.push({ type: 'video', url: submission.videoUrl });
	}

	const current = mediaItems[index] || mediaItems[0];
	const hasMultiple = mediaItems.length > 1;

	const handlePrev = (e) => {
		e.stopPropagation();
		setIndex((i) => (i > 0 ? i - 1 : mediaItems.length - 1));
	};

	const handleNext = (e) => {
		e.stopPropagation();
		setIndex((i) => (i < mediaItems.length - 1 ? i + 1 : 0));
	};

	return (
		<CardContainer onClick={() => onClick(submission)}>
			<ThumbnailWrapper>
				{current?.type === 'video' ? (
					<VideoThumb />
				) : current?.url ? (
					<ThumbnailImg
						src={current.url}
						alt={submission.name}
						loading="lazy"
					/>
				) : null}

				{hasMultiple && (
					<>
						<CardNav $left onClick={handlePrev}>
							‹
						</CardNav>
						<CardNav onClick={handleNext}>›</CardNav>
						<CardDots>
							{mediaItems.map((_, i) => (
								<CardDot key={i} $active={i === index} />
							))}
						</CardDots>
					</>
				)}
			</ThumbnailWrapper>
			<CardBody>
				<Name>{submission.name}</Name>
				<OrderId>Order #{submission.orderId}</OrderId>
				<OrderId>{new Date(submission.createdAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</OrderId>
				<Badge
					variant={STATUS_VARIANT[submission.status] || 'secondary'}
					label={submission.status}
				/>
			</CardBody>
		</CardContainer>
	);
}
